import { SecondaryButton, WarningButton } from 'components/Buttons';
import { Switch } from 'components/Forms/Switch';
import { filterDeep } from 'deepdash-es/standalone';
import { useGetDocumentTagData } from 'pages/tags/hooks/useGetDocumentTag';
import { useCallback, useMemo } from 'react';
import { Col, Container, Form, Modal, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { useGetBaseVariant } from '../documents_v2/hooks/useGetBaseVariant';
import HelperFunctions from '../global/HelperFunctions';
import { LoadingSpinner as LoadingSpinner2 } from '../global/Spinner';
import { DocumentTagForm } from './edit/DocumentTagForm';
import { useTagFormHandlers } from './hooks/useTagFormHandlers';
import { useTagFormState } from './hooks/useTagFormState';

/**
 * @param {number} documentId
 * @param {number | undefined} tagId
 * @param {string} tagName
 * @param {function():void} closeModal
 * @param {number[]} filterByVariants
 */
export function BlocksTagsModal({ documentId, tagId, tagName, closeModal, filterByVariants = [] }) {
    const { t } = useTranslation('documents');
    const documentTagData = useGetDocumentTagData(documentId, tagId, tagId === undefined);

    /** @type {import('models/documents.models').Tag | undefined} */
    const tag = documentTagData.currentData;

    const { state, setState, dirty, handleFormSubmit } = useTagFormState({
        documentId,
        tag,
        mergeTagValueIntoBase: true,
    });

    return (
        <Modal show={true} onHide={closeModal} scrollable={true} className="modal-fullscreen">
            <Modal.Header closeButton>
                <Modal.Title>
                    {t('document.navbar.main.editor.left.blocks.modals.blocksTagModal.editTag')}:&nbsp;{tagName}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form id="tag-variant-form" autoComplete="off">
                    {tag && (
                        <BlocksTagsModalInner
                            documentId={documentId}
                            filterByVariants={filterByVariants}
                            setState={setState}
                            state={state}
                        />
                    )}
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <LoadingSpinner2 isSubmitting={state.saveInProgress} />
                <SecondaryButton onClick={closeModal}>{t('btn.cancel')}</SecondaryButton>
                <WarningButton
                    disabled={state.saveInProgress || !dirty || documentTagData.isLoading}
                    type="button"
                    form="tag-variant-form"
                    onClick={handleFormSubmit}
                >
                    {t('btn.save')}
                </WarningButton>
            </Modal.Footer>
        </Modal>
    );
}

/**
 * @param {number} documentId
 * @param {number[]} filterByVariants
 * @param {{formData: import('models/documents.models').Tag | undefined, saveInProgress: boolean, showAllVariants: boolean}} state immer state of the parent modal
 * @param {function(function(object):void):void} setState immer state updater
 */
function BlocksTagsModalInner({ documentId, filterByVariants = [], state, setState }) {
    const { t } = useTranslation('documents');
    const baseVariant = useGetBaseVariant(undefined, true);

    const isVariantIncluded = useCallback(
        (variantId) => (state.showAllVariants ? true : filterByVariants.includes(variantId)),
        [state.showAllVariants, filterByVariants],
    );
    const handlers = useTagFormHandlers(state, setState, isVariantIncluded);

    const filteredDocumentVariants = useMemo(() => {
        if (filterByVariants.length === 0 || state.showAllVariants) {
            return HelperFunctions.variantAsList(baseVariant);
        }

        const filteredVariants = filterDeep(
            baseVariant,
            (variantValue) => {
                if (variantValue.variantGroup) {
                    return undefined;
                }

                return filterByVariants.includes(variantValue.id) ? true : undefined;
            },
            {
                childrenPath: ['children'],
            },
        );

        return HelperFunctions.variantAsList(filteredVariants);
    }, [baseVariant, state.showAllVariants, filterByVariants]);

    return (
        <>
            {filterByVariants.length > 0 && (
                <Container fluid>
                    <Row>
                        <Col>
                            <div className="mb-4">
                                <Switch
                                    label={t(
                                        'documents:document.navbar.main.editor.left.blocks.modals.blocksTagModal.showAllVariants',
                                    )}
                                    name="blocks-tag-modal-toggle-variants"
                                    checked={state.showAllVariants}
                                    onChange={() => {
                                        setState((draft) => {
                                            draft.showAllVariants = !draft.showAllVariants;
                                        });
                                    }}
                                />
                            </div>
                        </Col>
                    </Row>
                </Container>
            )}

            <DocumentTagForm
                documentId={documentId}
                formData={state.formData}
                variants={filteredDocumentVariants}
                columnTitles={{
                    variant: t(
                        'document.navbar.main.editor.left.blocks.modals.blocksTagModal.btn.columnTitles.variant',
                    ),
                    text: t('document.navbar.main.editor.left.blocks.modals.blocksTagModal.btn.columnTitles.text'),
                    link: t('document.navbar.main.editor.left.blocks.modals.blocksTagModal.btn.columnTitles.link'),
                    title: t('documents:document.navbar.tags.editModal.modal.title'),
                    reference: t(
                        'document.navbar.main.editor.left.blocks.modals.blocksTagModal.btn.columnTitles.reference',
                    ),
                }}
                {...handlers}
            />
        </>
    );
}
